import { Button } from '@components/Button'
import { toast } from '@components/Toaster'
import {
	Box,
	ButtonIcon,
	Callout,
	Form,
	IconSolidCheveronLeft,
	Stack,
	Text,
} from '@highlight-run/ui/components'
import { vars } from '@highlight-run/ui/vars'
import { SIGN_IN_ROUTE } from '@pages/Auth/AuthRouter'
import { AuthBody, AuthFooter, AuthHeader } from '@pages/Auth/Layout'
import analytics from '@util/analytics'
import { auth } from '@util/auth'
import { validateEmail } from '@util/string'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

export const ResetPassword: React.FC = () => {
	const navigate = useNavigate()
	const location = useLocation()
	const [searchParams] = useSearchParams()
	const token = searchParams.get('token')
	const initialEmail: string = location.state?.email ?? ''
	const [loading, setLoading] = useState(false)

	// Token mode: user clicked a reset link and is setting a new password.
	if (token) {
		return (
			<SetPasswordForm
				token={token}
				onDone={() => navigate(SIGN_IN_ROUTE)}
			/>
		)
	}

	// Email mode: user requested a reset link.
	const formStore = Form.useStore({
		defaultValues: {
			email: initialEmail ?? '',
		},
	})
	const email = formStore.useValue('email')

	useEffect(() => analytics.page('Reset Password'), [])

	return (
		<Form
			store={formStore}
			resetOnSubmit={false}
			onSubmit={() => {
				analytics.track('Reset password submission')

				if (!validateEmail(email)) {
					toast.warning('Please enter a valid email.')
					analytics.track('Reset password submission error')
					setLoading(false)
					return
				}

				setLoading(true)
				auth.sendPasswordResetEmail(email)
					.catch(() => {
						// swallow error if user does not exist
						analytics.track('Reset password submission error')
						setLoading(false)
					})
					.finally(() => {
						toast.success(
							'Password reset email sent (if a user exists)!',
						)

						setTimeout(() => {
							navigate(SIGN_IN_ROUTE, {
								state: { email },
							})
						}, 1000)
					})
			}}
		>
			<AuthHeader px="10" py="4">
				<Stack justify="space-between" align="center" direction="row">
					<Box display="flex" style={{ width: 30 }}>
						<ButtonIcon
							kind="secondary"
							emphasis="low"
							size="xSmall"
							icon={
								<IconSolidCheveronLeft
									color={vars.theme.static.content.weak}
								/>
							}
							onClick={() => {
								navigate(SIGN_IN_ROUTE, {
									state: {
										email,
									},
								})
							}}
						/>
					</Box>
					<Text color="moderate">Reset your password</Text>
					<Box style={{ width: 30 }}></Box>
				</Stack>
			</AuthHeader>
			<AuthBody>
				<Stack gap="12">
					<Form.Input
						name={formStore.names.email}
						label="Email"
						type="email"
						autoFocus
					/>
				</Stack>
			</AuthBody>
			<AuthFooter>
				<Button
					trackingId="sign-up-submit"
					loading={loading}
					type="submit"
				>
					Reset password
				</Button>
			</AuthFooter>
		</Form>
	)
}

const SetPasswordForm: React.FC<{ token: string; onDone: () => void }> = ({
	token,
	onDone,
}) => {
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')

	const formStore = Form.useStore({
		defaultValues: {
			password: '',
			confirm: '',
		},
	})
	const password = formStore.useValue('password')
	const confirm = formStore.useValue('confirm')

	useEffect(() => analytics.page('Set Password'), [])

	return (
		<Form
			store={formStore}
			resetOnSubmit={false}
			onSubmit={() => {
				if (password.length < 8) {
					setError('Password must be at least 8 characters.')
					return
				}
				if (password !== confirm) {
					setError('Passwords do not match.')
					return
				}

				setLoading(true)
				setError('')
				auth.setPassword(token, password)
					.then(() => {
						toast.success('Password set! You can now sign in.')
						setTimeout(onDone, 1000)
					})
					.catch((e) => {
						setError(
							e?.message ||
								'Failed to set password. The link may be invalid or expired.',
						)
						setLoading(false)
					})
			}}
		>
			<AuthHeader px="10" py="4">
				<Stack justify="space-between" align="center" direction="row">
					<Box display="flex" style={{ width: 30 }}>
						<ButtonIcon
							kind="secondary"
							emphasis="low"
							size="xSmall"
							icon={
								<IconSolidCheveronLeft
									color={vars.theme.static.content.weak}
								/>
							}
							onClick={onDone}
						/>
					</Box>
					<Text color="moderate">Set a new password</Text>
					<Box style={{ width: 30 }}></Box>
				</Stack>
			</AuthHeader>
			<AuthBody>
				<Stack gap="12">
					<Form.Input
						name={formStore.names.password}
						label="New password"
						type="password"
						autoFocus
						autoComplete="new-password"
					/>
					<Form.Input
						name={formStore.names.confirm}
						label="Confirm password"
						type="password"
						autoComplete="new-password"
					/>
					{error && <Callout kind="error">{error}</Callout>}
				</Stack>
			</AuthBody>
			<AuthFooter>
				<Button
					trackingId="set-password-submit"
					loading={loading}
					type="submit"
				>
					Set password
				</Button>
			</AuthFooter>
		</Form>
	)
}
